import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
  CircularProgress,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

import { SAVED_FILTER_NAME_MAX_LENGTH } from '../constants';

import './styles.scss';

const saveFilterSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .max(SAVED_FILTER_NAME_MAX_LENGTH, `Filter Name must be ${SAVED_FILTER_NAME_MAX_LENGTH} characters or fewer`)
    .required('Filter Name is required'),
});

const SaveFilterModal = ({
  open,
  onClose,
  initialName = '',
  isEditMode = false,
  isLoading = false,
  onSubmit,
}) => {
  const formId = 'save-filter-form';

  return (
    <Formik
      key={`save-filter-${open ? 'open' : 'closed'}-${isEditMode ? 'edit' : 'create'}-${initialName}`}
      initialValues={{ name: initialName || '' }}
      validationSchema={saveFilterSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        const trimmedName = values.name.trim();

        if (!trimmedName) {
          setSubmitting(false);
          return;
        }

        onSubmit({ name: trimmedName });
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <OlcFormModal
          open={open}
          onClose={onClose}
          title={isEditMode ? 'Update Filter' : 'Save Filter'}
          subtitle="Give this filter set a name so you can reuse it."
          className="saveFilterFormModal"
          footer={(
            <>
              <OlcButton variant="secondary" onClick={onClose} type="button">
                Cancel
              </OlcButton>
              <OlcButton
                variant="primary"
                disabled={isLoading || isSubmitting}
                type="submit"
                form={formId}
              >
                {isLoading ? (
                  <CircularProgress
                    sx={{
                      color: '#fff',
                      width: '20px !important',
                      height: '20px !important',
                    }}
                  />
                ) : (
                  'Save'
                )}
              </OlcButton>
            </>
          )}
        >
          <form id={formId} className="saveFilterModalForm" onSubmit={handleSubmit} noValidate>
            <InputLabel htmlFor="save-filter-name-input" className="inputLabel formLabel">
              Filter Name<span className="olcRequired">*</span>
            </InputLabel>
            <TextField
              id="save-filter-name-input"
              placeholder="Filter Name"
              fullWidth
              autoFocus
              autoComplete="off"
              name="name"
              className="inputField formField"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(errors.name && touched.name)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                    {values.name ? (
                      <IconButton
                        className="cpClearInputBtn"
                        size="small"
                        onClick={() => setFieldValue('name', '')}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
            {touched.name && errors.name ? (
              <Typography className="errorMessage">{errors.name}</Typography>
            ) : null}
          </form>
        </OlcFormModal>
      )}
    </Formik>
  );
};

export default SaveFilterModal;
